interface AnyObject {
    [key: string]: any;
}
type AnyArray = unknown[];

let logsEnabled = false;
let currentLogContext = 'Comparison';

const DIFF_LOG_STYLES = {
    header: 'color: #ff6b6b; font-weight: bold; font-size: 12px;',
    subHeader: 'color: #4ecdc4; font-weight: bold;',
    success: 'color: #2ecc71; font-weight: bold;',
    warning: 'color: #f39c12; font-weight: bold;',
    error: 'color: #e74c3c; font-weight: bold;',
    data: 'color: #3498db;',
    highlight: 'color: #9b59b6; font-weight: bold;',
    muted: 'color: #7f8c8d;',
    keyAction: 'color: #e67e22;',
};

function log(message: string, style: string = DIFF_LOG_STYLES.muted, ...args: any[]): void {
    if (logsEnabled) {
        console.log(`%c${message}`, style, ...args);
    }
}

function logWithData(message: string, data: any, style: string = DIFF_LOG_STYLES.data): void {
    if (logsEnabled) {
        console.log(`%c${message}`, style, JSON.stringify(data, null, 2));
    }
}

function formatValueForLog(value: any): string {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value === 'string') return `"${value.length > 50 ? value.substring(0, 50) + '...' : value}"`;
    if (typeof value === 'object') {
        if (Array.isArray(value)) return `Array(${value.length})`;
        const keys = Object.keys(value);
        return `{${keys.slice(0, 3).join(', ')}${keys.length > 3 ? `, ...+${keys.length - 3}` : ''}}`;
    }
    return String(value);
}

function logDiffResult(differences: string[], additions: string[], removals: string[]): void {
    const totalChanges = differences.length + additions.length + removals.length;

    if (totalChanges === 0) {
        console.log(`%c✅ No differences found`, DIFF_LOG_STYLES.success);
    } else {
        console.log(`%c📊 Changes Summary: ${totalChanges} change(s)`, DIFF_LOG_STYLES.subHeader);

        if (differences.length > 0) {
            console.groupCollapsed(`%c🔄 Modified: ${differences.length} field(s)`, DIFF_LOG_STYLES.warning);
            differences.forEach((diff) => {
                console.log(`%c${diff}`, DIFF_LOG_STYLES.warning);
            });
            console.groupEnd();
        }
        if (additions.length > 0) {
            console.groupCollapsed(`%c➕ Added: ${additions.length} field(s) (in Changed but not in Original)`, DIFF_LOG_STYLES.success);
            additions.forEach((add) => {
                console.log(`%c${add}`, DIFF_LOG_STYLES.success);
            });
            console.groupEnd();
        }
        if (removals.length > 0) {
            console.groupCollapsed(`%c➖ Removed: ${removals.length} field(s) (in Original but not in Changed)`, DIFF_LOG_STYLES.error);
            removals.forEach((rem) => {
                console.log(`%c${rem}`, DIFF_LOG_STYLES.error);
            });
            console.groupEnd();
        }
    }
}

function getNestedValue(obj: AnyObject, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
}

function collectDiffDetails(
    diffResult: AnyObject,
    originalObj: AnyObject,
    path: string = '',
): { differences: string[]; additions: string[]; removals: string[] } {
    const differences: string[] = [];
    const additions: string[] = [];
    const removals: string[] = [];

    Object.keys(diffResult).forEach((key) => {
        const currentPath = path ? `${path}.${key}` : key;
        const newValue = diffResult[key];
        const originalValue = getNestedValue(originalObj, currentPath);

        if (newValue === null) {
            removals.push(`${currentPath}: ${formatValueForLog(originalValue)} → null`);
        } else if (typeof newValue === 'object' && !Array.isArray(newValue)) {
            const nested = collectDiffDetails(newValue, originalObj, currentPath);
            differences.push(...nested.differences);
            additions.push(...nested.additions);
            removals.push(...nested.removals);
        } else if (originalValue === undefined) {
            additions.push(`${currentPath}: undefined → ${formatValueForLog(newValue)}`);
        } else {
            differences.push(`${currentPath}: ${formatValueForLog(originalValue)} → ${formatValueForLog(newValue)}`);
        }
    });

    return { differences, additions, removals };
}

function isEmptyObjectRecursive(obj: any, path: string = 'root'): boolean {
    if (obj === null || obj === undefined) {
        log(
            `[isEmptyObjectRecursive] path="${path}" is null/undefined → returning false (not empty, it's a real null/undefined value)`,
            DIFF_LOG_STYLES.muted,
        );
        return false;
    }

    if (typeof obj !== 'object') {
        log(
            `[isEmptyObjectRecursive] path="${path}" is primitive type (${typeof obj}) with value=${JSON.stringify(obj)} → returning false (has a value)`,
            DIFF_LOG_STYLES.muted,
        );
        return false;
    }

    if (Array.isArray(obj)) {
        const result = obj.length === 0 || obj.every((item, idx) => isEmptyObjectRecursive(item, `${path}[${idx}]`));
        log(`[isEmptyObjectRecursive] path="${path}" is Array(${obj.length}) → isEmpty=${result}`, DIFF_LOG_STYLES.highlight);
        return result;
    }

    const keys = Object.keys(obj);
    if (keys.length === 0) {
        log(`[isEmptyObjectRecursive] path="${path}" is empty object {} → returning true`, DIFF_LOG_STYLES.success);
        return true;
    }

    const result = keys.every((key) => isEmptyObjectRecursive(obj[key], `${path}.${key}`));
    log(`[isEmptyObjectRecursive] path="${path}" has keys [${keys.join(', ')}] → isEmpty=${result}`, DIFF_LOG_STYLES.highlight);
    return result;
}

function removeEmptyObjectsRecursive(obj: AnyObject, path: string = 'root'): AnyObject {
    log(`[removeEmptyObjectsRecursive] ENTER path="${path}"`, DIFF_LOG_STYLES.keyAction);
    logWithData(`[removeEmptyObjectsRecursive] INPUT at path="${path}":`, obj, DIFF_LOG_STYLES.data);

    const result: AnyObject = {};

    Object.keys(obj).forEach((key) => {
        const value = obj[key];
        const keyPath = `${path}.${key}`;

        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
            const cleaned = removeEmptyObjectsRecursive(value, keyPath);
            const isEmpty = isEmptyObjectRecursive(cleaned, keyPath);
            if (!isEmpty) {
                log(
                    `[removeEmptyObjectsRecursive] KEEPING nested object key="${key}" at path="${keyPath}" (not empty after cleaning)`,
                    DIFF_LOG_STYLES.success,
                );
                result[key] = cleaned;
            } else {
                log(
                    `[removeEmptyObjectsRecursive] REMOVING nested object key="${key}" at path="${keyPath}" (empty after cleaning)`,
                    DIFF_LOG_STYLES.error,
                );
            }
        } else if (!isEmptyObjectRecursive(value, keyPath)) {
            log(
                `[removeEmptyObjectsRecursive] KEEPING non-object key="${key}" at path="${keyPath}" with value=${JSON.stringify(value)}`,
                DIFF_LOG_STYLES.success,
            );
            result[key] = value;
        } else {
            log(`[removeEmptyObjectsRecursive] REMOVING key="${key}" at path="${keyPath}" (isEmpty=true)`, DIFF_LOG_STYLES.error);
        }
    });

    logWithData(`[removeEmptyObjectsRecursive] OUTPUT at path="${path}":`, result, DIFF_LOG_STYLES.success);
    return result;
}

function isEquivalent(a: any, b: any, strict: boolean, looseStringNumber: boolean = false): boolean {
    if (looseStringNumber) {
        const aIsString = typeof a === 'string';
        const bIsString = typeof b === 'string';
        const aIsNumber = typeof a === 'number';
        const bIsNumber = typeof b === 'number';

        if ((aIsString && bIsNumber) || (aIsNumber && bIsString)) {
            const result = String(a) === String(b);
            log(
                `[isEquivalent] looseStringNumber comparison: "${a}" (${typeof a}) vs "${b}" (${typeof b}) → ${result}`,
                result ? DIFF_LOG_STYLES.success : DIFF_LOG_STYLES.warning,
            );
            return result;
        }
    }

    if (strict) {
        const result = a === b;
        return result;
    }

    const equivalents = ['', undefined, null, NaN];
    return equivalents.includes(a) && equivalents.includes(b) ? true : a == b;
}

function diffObjects(
    original: AnyObject,
    changed: AnyObject,
    strict: boolean,
    looseStringNumber: boolean,
    path: string = 'root',
): AnyObject {
    const diffObject: AnyObject = {};

    if (logsEnabled) {
        console.group(`%c[diffObjects] ${path}`, DIFF_LOG_STYLES.highlight);
        console.log(`%cOriginal keys: [${Object.keys(original).join(', ')}]`, DIFF_LOG_STYLES.data);
        console.log(`%cChanged keys: [${Object.keys(changed).join(', ')}]`, DIFF_LOG_STYLES.data);
        console.groupCollapsed(`%cFull objects (click to expand)`, DIFF_LOG_STYLES.muted);
        console.log('Original:', original);
        console.log('Changed:', changed);
        console.groupEnd();
    }

    for (const key of Object.keys(original)) {
        const keyPath = `${path}.${key}`;
        const originalValue = original[key];
        const changedValue = changed[key];

        log(`[diffObjects] Processing key="${key}" at path="${keyPath}"`, DIFF_LOG_STYLES.keyAction);
        log(`[diffObjects]   Original["${key}"] = ${JSON.stringify(originalValue)}`, DIFF_LOG_STYLES.data);
        log(`[diffObjects]   Changed["${key}"] = ${JSON.stringify(changedValue)}`, DIFF_LOG_STYLES.data);

        if (originalValue !== undefined && changedValue === undefined && strict) {
            log(
                `[diffObjects]   → REMOVED: key="${key}" exists in Original but NOT in Changed (strict mode) → marking as null`,
                DIFF_LOG_STYLES.error,
            );
            diffObject[key] = null;
        } else if (
            (originalValue !== null || changedValue !== null) &&
            typeof originalValue === 'object' &&
            typeof changedValue === 'object'
        ) {
            log(`[diffObjects]   → NESTED: Both are objects, recursing into key="${key}"...`, DIFF_LOG_STYLES.highlight);
            const subDiffResult = getDiff(originalValue, changedValue, strict, looseStringNumber);
            if (!subDiffResult || Object.keys(subDiffResult).length > 0) {
                log(`[diffObjects]   → NESTED DIFF FOUND for key="${key}":`, DIFF_LOG_STYLES.warning);
                logWithData(`[diffObjects]   Nested diff result:`, subDiffResult, DIFF_LOG_STYLES.warning);
                diffObject[key] = subDiffResult;
            } else {
                log(`[diffObjects]   → NESTED EQUAL: key="${key}" nested objects are identical`, DIFF_LOG_STYLES.success);
            }
        } else if (!isEquivalent(originalValue, changedValue, strict, looseStringNumber)) {
            log(`[diffObjects]   → MODIFIED: key="${key}" values are DIFFERENT`, DIFF_LOG_STYLES.warning);
            log(`[diffObjects]     Original value: ${JSON.stringify(originalValue)} (${typeof originalValue})`, DIFF_LOG_STYLES.error);
            log(`[diffObjects]     Changed value: ${JSON.stringify(changedValue)} (${typeof changedValue})`, DIFF_LOG_STYLES.success);
            diffObject[key] = changedValue;
        } else {
            log(`[diffObjects]   → EQUAL: key="${key}" values are equivalent (no diff)`, DIFF_LOG_STYLES.success);
        }
        delete changed[key];
    }

    for (const key of Object.keys(changed)) {
        const keyPath = `${path}.${key}`;
        log(`[diffObjects] ADDED: key="${key}" at path="${keyPath}" exists ONLY in Changed (not in Original)`, DIFF_LOG_STYLES.success);
        log(`[diffObjects]   Added value: ${JSON.stringify(changed[key])}`, DIFF_LOG_STYLES.success);
        diffObject[key] = changed[key];
    }

    if (logsEnabled) {
        if (Object.keys(diffObject).length > 0) {
            console.log(`%c📊 Diff result: ${Object.keys(diffObject).length} difference(s)`, DIFF_LOG_STYLES.warning, diffObject);
        } else {
            console.log(`%c✅ No differences`, DIFF_LOG_STYLES.success);
        }
        console.groupEnd();
    }

    return diffObject;
}

function diffArrays(original: AnyArray, changed: AnyArray, strict: boolean, looseStringNumber: boolean, path: string = 'root'): AnyArray {
    const diffArray: unknown[] = [];
    let indexOffset = 0;

    if (logsEnabled) {
        console.group(`%c[diffArrays] ${path} (${original.length} vs ${changed.length} items)`, DIFF_LOG_STYLES.highlight);
        console.groupCollapsed(`%cFull arrays (click to expand)`, DIFF_LOG_STYLES.muted);
        console.table(original);
        console.table(changed);
        console.groupEnd();
    }

    if (changed.length === 0) {
        log(`[diffArrays] Changed array is empty → returning original array as diff`, DIFF_LOG_STYLES.warning);
        return original as unknown as AnyArray;
    }

    for (let i = 0; i < original.length; i++) {
        const originalElement = original[i];
        const changedElement = changed[i - indexOffset];
        const elementPath = `${path}[${i}]`;

        log(`[diffArrays] Processing index ${i} at path="${elementPath}"`, DIFF_LOG_STYLES.keyAction);
        log(`[diffArrays]   Original[${i}] = ${JSON.stringify(originalElement)}`, DIFF_LOG_STYLES.data);
        log(`[diffArrays]   Changed[${i - indexOffset}] = ${JSON.stringify(changedElement)}`, DIFF_LOG_STYLES.data);

        if (originalElement && changedElement && typeof originalElement === 'object' && typeof changedElement === 'object') {
            log(`[diffArrays]   → NESTED: Both are objects, recursing...`, DIFF_LOG_STYLES.highlight);
            const subDiffResult = getDiff(
                originalElement as AnyObject | AnyArray,
                changedElement as AnyObject | AnyArray,
                strict,
                looseStringNumber,
            );
            if (Object.keys(subDiffResult).length > 0) {
                log(`[diffArrays]   → NESTED DIFF FOUND at index ${i}:`, DIFF_LOG_STYLES.warning);
                logWithData(`[diffArrays]   Nested diff:`, subDiffResult, DIFF_LOG_STYLES.warning);
                diffArray.push(subDiffResult);
            } else {
                log(`[diffArrays]   → NESTED EQUAL: index ${i} objects are identical`, DIFF_LOG_STYLES.success);
            }
        } else if (!isEquivalent(originalElement, changedElement, strict, looseStringNumber)) {
            log(`[diffArrays]   → MODIFIED: index ${i} values are DIFFERENT`, DIFF_LOG_STYLES.warning);
            log(`[diffArrays]     Original: ${JSON.stringify(originalElement)}`, DIFF_LOG_STYLES.error);
            log(`[diffArrays]     Changed: ${JSON.stringify(changedElement)}`, DIFF_LOG_STYLES.success);
            diffArray.push(changedElement);
        } else {
            log(`[diffArrays]   → EQUAL: index ${i} values are equivalent`, DIFF_LOG_STYLES.success);
        }

        changed.splice(i - indexOffset, 1);
        indexOffset++;
    }

    if (changed.length > 0) {
        log(`[diffArrays] ADDED: ${changed.length} extra elements in Changed array (not in Original)`, DIFF_LOG_STYLES.success);
        logWithData(`[diffArrays] Extra elements:`, changed, DIFF_LOG_STYLES.success);
        diffArray.push(...changed);
    }

    if (logsEnabled) {
        if (diffArray.length > 0) {
            console.log(`%c📊 Diff result: ${diffArray.length} difference(s)`, DIFF_LOG_STYLES.warning, diffArray);
        } else {
            console.log(`%c✅ No differences`, DIFF_LOG_STYLES.success);
        }
        console.groupEnd();
    }

    return diffArray;
}

export function getDiff(
    original: AnyObject | AnyArray,
    changed: AnyObject | AnyArray,
    strict: boolean = true,
    looseStringNumber: boolean = false,
    excludeEmptyObjects: boolean = false,
    enableLogs: boolean = false,
    logContext: string = 'Comparison',
): AnyObject | AnyArray {
    const isTopLevelCall = !logsEnabled && enableLogs;

    if (isTopLevelCall) {
        logsEnabled = true;
        currentLogContext = logContext;
    }

    if (typeof original !== typeof changed && typeof original !== 'object') {
        throw new TypeError('A diff cannot be made between two different types. Please check the values you passed in.');
    }

    if (isTopLevelCall) {
        console.group(`%c🔍 getDiff: ${logContext}`, DIFF_LOG_STYLES.header);

        console.groupCollapsed(`%c📥 Original (baseline/source - comparing FROM this)`, DIFF_LOG_STYLES.subHeader);
        console.dir(original);
        console.groupEnd();

        console.groupCollapsed(`%c📤 Changed (current/target - comparing TO this)`, DIFF_LOG_STYLES.subHeader);
        console.dir(changed);
        console.groupEnd();

        console.log(
            `%c🔧 Options: strict=${strict}, looseStringNumber=${looseStringNumber}, excludeEmptyObjects=${excludeEmptyObjects}`,
            DIFF_LOG_STYLES.muted,
        );
    }

    const result = Array.isArray(original)
        ? diffArrays(original ?? [], [...((changed ?? []) as AnyArray)], strict, looseStringNumber, logContext)
        : diffObjects({ ...((original ?? {}) as AnyObject) }, { ...((changed ?? {}) as AnyObject) }, strict, looseStringNumber, logContext);

    if (isTopLevelCall) {
        log(`[getDiff] RAW diff result (before excludeEmptyObjects):`, DIFF_LOG_STYLES.header);
        logWithData(`[getDiff] Raw result:`, result, DIFF_LOG_STYLES.warning);
    }

    let finalResult = result;
    if (excludeEmptyObjects && !Array.isArray(result)) {
        if (isTopLevelCall) {
            log(`[getDiff] Applying excludeEmptyObjects filter...`, DIFF_LOG_STYLES.highlight);
        }
        finalResult = removeEmptyObjectsRecursive(result);
        if (isTopLevelCall) {
            log(`[getDiff] FINAL result (after excludeEmptyObjects):`, DIFF_LOG_STYLES.header);
            logWithData(`[getDiff] Final result:`, finalResult, DIFF_LOG_STYLES.success);
        }
    }

    if (isTopLevelCall && !Array.isArray(finalResult)) {
        const { differences, additions, removals } = collectDiffDetails(finalResult as AnyObject, original as AnyObject);
        logDiffResult(differences, additions, removals);
        console.groupEnd();
        logsEnabled = false;
    } else if (isTopLevelCall) {
        console.log(`%c📊 Result: Array with ${(finalResult as AnyArray).length} differences`, DIFF_LOG_STYLES.data);
        console.groupEnd();
        logsEnabled = false;
    }

    return finalResult;
}
